import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';

import TextInput from './TextInput.jsx';
import GenericSelect from './GenericSelect';
import CheckboxInput from './CheckboxInput';
import EntitlementRulesEditor from './EntitlementRulesEditor';
import Button from './Button';
import { isMonthly, isCarryOverLocked, canUseCarryOver, MAX_ELIGIBILITY_MONTHS, maxExpiryMonthsFor } from '../../utils/leaveAccrual';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ItemCard = styled.div`
  border: 1px solid #e8ecf0;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fafbfc;
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ItemIndex = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const RemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: none;
  color: #d9534f;
  cursor: pointer;
  &:hover { background: #fdecea; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CheckRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #d9534f;
`;

const Hint = styled.div`
  font-size: 12px;
  line-height: 1.5;
  padding: 8px 12px;
  border-radius: 6px;
  background: ${({ $tone }) => ($tone === 'warning' ? '#FFF8E6' : '#EEF6FD')};
  border-left: 3px solid ${({ $tone }) => ($tone === 'warning' ? '#FFC107' : '#0291EF')};
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Repeatable-card editor for LeavePolicyTemplateRequest.Variations — bir tür
// listesi, GenericFormDrawer'ın 'leavePolicyTemplateVariations' alan tipi
// üzerinden şablon FORMUNUN İÇİNE gömülür (ayrı bir sayfa/rota DEĞİL). Backend
// tek bir PUT/POST çağrısında tüm şablonu (Name/CountryCode/Description +
// Variations[]) aldığı için (LeavePolicyTemplateController — LeavePolicy'nin
// aksine variation-bazlı ayrı uç YOK), tür listesi ayrı bir CRUD ekranı değil,
// TEK formun bir parçası olarak kurgulanmalı. Desen TaskTemplateItemsEditor'dan
// (components/ui/TaskTemplateItemsEditor.js) alınmıştır — kart-bazlı ekle/sil.
//
// Alanlar LeavePolicyTemplateVariation modeliyle birebir aynı (LeaveVariation'ın
// bir kopyası) — etiketler için yeni anahtar açmak yerine mevcut
// `leaveVariation.*` çeviri ailesi (leave-types sayfasıyla PAYLAŞILIR) yeniden
// kullanılır; yalnızca kart başlığı/ekle butonu `leavePolicyTemplate.*` altında
// yeni anahtarlar taşır.
// `countryCode`: şablonun ülkesi ('' = ülkesiz). Türkiye şablonunda yasal yıllık izne devir
// tavanı alanları gizlenir — backend LeaveAccrualValidator da reddeder.
export default function LeavePolicyTemplateVariationsEditor({ label, value, onChange, error, countryCode = '' }) {
  const { t } = useTranslation();
  const items = Array.isArray(value) ? value : [];

  const genderOptions = [
    { id: 'all', name: t('leaveVariation.genderAll') },
    { id: 'male', name: t('leaveVariation.genderMale') },
    { id: 'female', name: t('leaveVariation.genderFemale') },
  ];

  // Bakiye ve sınıf ekseni — leave-types/config/index.js'teki getFormSchema
  // ile ALAN ALAN aynı kalmalı (kiracı varyasyonu ile şablon varyasyonu aynı
  // modeli paylaşıyor). "Mazeret izni" gibi tek bir onay kutusu YOK.
  const balanceOptions = [
    { id: 'annual', name: t('leaveVariation.balanceAnnual') },
    { id: 'own', name: t('leaveVariation.balanceOwn') },
    { id: 'none', name: t('leaveVariation.balanceNone') },
  ];

  const categoryOptions = [
    { id: 'annual', name: t('leaveVariation.categoryAnnual') },
    { id: 'statutory', name: t('leaveVariation.categoryStatutory') },
    { id: 'medical', name: t('leaveVariation.categoryMedical') },
    { id: 'maternity', name: t('leaveVariation.categoryMaternity') },
    { id: 'benefit', name: t('leaveVariation.categoryBenefit') },
    { id: 'unpaid', name: t('leaveVariation.categoryUnpaid') },
  ];

  const entitlementBasisOptions = [
    { id: 'perYear', name: t('leaveVariation.basisPerYear') },
    { id: 'perEvent', name: t('leaveVariation.basisPerEvent') },
  ];

  const anchorOptions = [
    { id: 'none', name: t('leaveVariation.anchorNone') },
    { id: 'birthday', name: t('leaveVariation.anchorBirthday') },
  ];

  // Hak ediş biçimi — leave-types/config/index.js'teki getFormSchema ile ALAN ALAN aynı.
  const frequencyOptions = [
    { id: 'yearly', name: t('leaveVariation.accrualYearly') },
    { id: 'monthly', name: t('leaveVariation.accrualMonthly') },
  ];

  const roundingOptions = [
    { id: 'exact', name: t('leaveVariation.roundingExact') },
    { id: 'halfDown', name: t('leaveVariation.roundingHalfDown') },
  ];

  const updateItem = (index, patch) => {
    const next = items.map((it, i) => (i === index ? { ...it, ...patch } : it));
    onChange(next);
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  // balanceScope/category kasıtlı olarak varsayılansız bırakılır ('' — hiçbir
  // seçeneğe eşlenmez): backend LeaveAxisValidator ikisini de zorunlu kılıyor
  // (leaveVariation.invalidBalanceScope/categoryRequired) ve model binding'in
  // sessiz varsayılanı category='other' tam da bu editörün eskiden ürettiği,
  // incelemede bulunan sızıntı yoluydu — İK bilinçli bir seçim yapmadan
  // form gönderilemesin diye seçim zorunlu bırakılır.
  const addItem = () => {
    onChange([
      ...items,
      {
        name: '',
        description: '',
        forGender: 'all',
        instanceLimit: '',
        monthlyLimit: '',
        negativeBalanceLimit: '',
        cooldownDays: '',
        isPaid: false,
        isHourly: false,
        fridayPlusOne: false,
        carriesForward: false,
        fullWeekPlusOne: false,
        entitlementRules: null,
        balanceScope: '',
        category: '',
        entitlementBasis: 'perYear',
        eligibilityAnchor: 'none',
        anchorToleranceDays: '',
        requiresDocument: false,
        payrollCode: '',
        accrualFrequency: 'yearly',
        accrualEligibilityMonths: 0,
        accrualTopUpAtAnniversary: false,
        accrualRounding: 'exact',
        carryOverCap: '',
        carryOverExpiryMonths: '',
      },
    ]);
  };

  return (
    <Wrapper>
      {label && <Label>{label}</Label>}
      {items.map((item, index) => (
        <ItemCard key={index}>
          <ItemHeader>
            <ItemIndex>{t('leavePolicyTemplate.variationIndex', { index: index + 1 })}</ItemIndex>
            <RemoveBtn type="button" onClick={() => removeItem(index)} aria-label={t('common.delete')}>
              <Trash2 size={16} />
            </RemoveBtn>
          </ItemHeader>

          <TextInput
            label={t('leaveVariation.name')}
            value={item.name || ''}
            onChange={(e) => updateItem(index, { name: e.target.value })}
            required
          />

          <TextInput
            label={t('leaveVariation.description')}
            type="textarea"
            value={item.description || ''}
            onChange={(e) => updateItem(index, { description: e.target.value })}
            rows={2}
          />

          <GenericSelect
            label={t('leaveVariation.forGender')}
            options={genderOptions}
            value={item.forGender || 'all'}
            onChange={(val) => updateItem(index, { forGender: val })}
          />

          <Row>
            <TextInput
              label={t('leaveVariation.instanceLimit')}
              type="number"
              value={item.instanceLimit ?? ''}
              onChange={(e) => updateItem(index, { instanceLimit: e.target.value })}
            />
            <TextInput
              label={t('leaveVariation.monthlyLimit')}
              type="number"
              value={item.monthlyLimit ?? ''}
              onChange={(e) => updateItem(index, { monthlyLimit: e.target.value })}
            />
          </Row>

          <Row>
            <TextInput
              label={t('leaveVariation.negativeBalanceLimit')}
              type="number"
              value={item.negativeBalanceLimit ?? ''}
              onChange={(e) => updateItem(index, { negativeBalanceLimit: e.target.value })}
            />
            <TextInput
              label={t('leaveVariation.cooldownDays')}
              type="number"
              value={item.cooldownDays ?? ''}
              onChange={(e) => updateItem(index, { cooldownDays: e.target.value })}
            />
          </Row>

          <CheckRow>
            <CheckboxInput
              label={t('leaveVariation.isPaid')}
              checked={!!item.isPaid}
              onChange={(val) => updateItem(index, { isPaid: val })}
            />
            <CheckboxInput
              label={t('leaveVariation.isHourly')}
              checked={!!item.isHourly}
              onChange={(val) => updateItem(index, { isHourly: val })}
            />
            <CheckboxInput
              label={t('leaveVariation.fridayPlusOne')}
              checked={!!item.fridayPlusOne}
              onChange={(val) => updateItem(index, { fridayPlusOne: val })}
            />
            <CheckboxInput
              label={t('leaveVariation.carriesForward')}
              checked={!!item.carriesForward}
              onChange={(val) => updateItem(index, { carriesForward: val })}
            />
            <CheckboxInput
              label={t('leaveVariation.fullWeekPlusOne')}
              checked={!!item.fullWeekPlusOne}
              onChange={(val) => updateItem(index, { fullWeekPlusOne: val })}
            />
          </CheckRow>

          <Row>
            <GenericSelect
              label={t('leaveVariation.balanceScope')}
              options={balanceOptions}
              value={item.balanceScope || null}
              onChange={(val) => updateItem(index, { balanceScope: val })}
              required
            />
            <GenericSelect
              label={t('leaveVariation.category')}
              options={categoryOptions}
              value={item.category || null}
              onChange={(val) => updateItem(index, { category: val })}
              required
            />
          </Row>

          <Row>
            <GenericSelect
              label={t('leaveVariation.entitlementBasis')}
              options={entitlementBasisOptions}
              value={item.entitlementBasis || 'perYear'}
              onChange={(val) => updateItem(index, { entitlementBasis: val })}
            />
            <GenericSelect
              label={t('leaveVariation.eligibilityAnchor')}
              options={anchorOptions}
              value={item.eligibilityAnchor || 'none'}
              // İnceleme turu (Task 10 düzeltmesi) — Important bulgu: çapa
              // 'birthday' dışına çevrilince tolerans alanı kartta GİZLENİR
              // (aşağıdaki koşullu render) ama eski değer item'da kalıp
              // gövdeye gidiyordu — LeaveAxisValidator toleransı yalnız çapa
              // 'birthday' iken kontrol ettiği için 400 dönmüyor, anlamsız
              // değer sessizce kaydediliyordu. leave-types/config/index.js'teki
              // eş alan deriveFields ile aynı düzeltmeyi burada elle uyguluyoruz
              // (bu editör GenericFormDrawer'ın deriveFields kancasını kullanmaz).
              // '' — bu dosyanın "boş sayısal alan" kuralı (addItem'ın tohumu,
              // preparePayload'daki VARIATION_NULLABLE_FIELDS'in '' → null
              // çevirdiği alan).
              onChange={(val) => updateItem(index, val === 'birthday'
                ? { eligibilityAnchor: val }
                : { eligibilityAnchor: val, anchorToleranceDays: '' })}
            />
          </Row>

          {item.eligibilityAnchor === 'birthday' && (
            <TextInput
              label={t('leaveVariation.anchorToleranceDays')}
              type="number"
              value={item.anchorToleranceDays ?? ''}
              onChange={(e) => updateItem(index, { anchorToleranceDays: e.target.value })}
            />
          )}

          <Row>
            <CheckboxInput
              label={t('leaveVariation.requiresDocument')}
              checked={!!item.requiresDocument}
              onChange={(val) => updateItem(index, { requiresDocument: val })}
            />
            <TextInput
              label={t('leaveVariation.payrollCode')}
              value={item.payrollCode || ''}
              onChange={(e) => updateItem(index, { payrollCode: e.target.value })}
            />
          </Row>

          <Row>
            <GenericSelect
              label={t('leaveVariation.accrualFrequency')}
              options={frequencyOptions}
              value={item.accrualFrequency || 'yearly'}
              // Yıllığa dönüşte aylık-özel ayarlar varsayılana iner (gizli alanda eski
              // değer kalıp gövdeye gitmesin — anchorToleranceDays ile aynı gerekçe).
              onChange={(val) => updateItem(index, val === 'monthly'
                ? { accrualFrequency: val }
                : { accrualFrequency: val, accrualEligibilityMonths: 0, accrualTopUpAtAnniversary: false, accrualRounding: 'exact' })}
            />
            {isMonthly(item) && (
              <GenericSelect
                label={t('leaveVariation.accrualRounding')}
                options={roundingOptions}
                value={item.accrualRounding || 'exact'}
                onChange={(val) => updateItem(index, { accrualRounding: val })}
              />
            )}
          </Row>

          {isMonthly(item) && (
            <>
              <Row>
                <TextInput
                  label={t('leaveVariation.accrualEligibilityMonths')}
                  type="number"
                  min={0}
                  max={MAX_ELIGIBILITY_MONTHS}
                  step={1}
                  value={item.accrualEligibilityMonths ?? ''}
                  onChange={(e) => updateItem(index, { accrualEligibilityMonths: e.target.value })}
                />
                <CheckboxInput
                  label={t('leaveVariation.accrualTopUpAtAnniversary')}
                  checked={!!item.accrualTopUpAtAnniversary}
                  onChange={(val) => updateItem(index, { accrualTopUpAtAnniversary: val })}
                />
              </Row>
              <Hint>{t('leaveVariation.monthlyAccrualHint')}</Hint>
            </>
          )}

          {canUseCarryOver(item, countryCode) && (
            <>
              <Row>
                <TextInput
                  label={t('leaveVariation.carryOverCap')}
                  type="number"
                  min={0}
                  step={0.5}
                  value={item.carryOverCap ?? ''}
                  onChange={(e) => updateItem(index, { carryOverCap: e.target.value })}
                />
                <TextInput
                  label={t('leaveVariation.carryOverExpiryMonths')}
                  type="number"
                  min={1}
                  max={maxExpiryMonthsFor(item)}
                  step={1}
                  value={item.carryOverExpiryMonths ?? ''}
                  onChange={(e) => updateItem(index, { carryOverExpiryMonths: e.target.value })}
                />
              </Row>
              <Hint>{t('leaveVariation.carryOverHint')}</Hint>
            </>
          )}

          {item.carriesForward && isCarryOverLocked(item, countryCode) && (
            <Hint $tone="warning">{t('leaveVariation.carryOverLockedTurkey')}</Hint>
          )}

          <EntitlementRulesEditor
            label={t('leaveVariation.entitlementRules')}
            value={item.entitlementRules ?? null}
            onChange={(val) => updateItem(index, { entitlementRules: val })}
          />
        </ItemCard>
      ))}

      {error && <ErrorText>{error}</ErrorText>}

      <Button type="button" variant="outline" onClick={addItem}>
        + {t('leavePolicyTemplate.addVariation')}
      </Button>
    </Wrapper>
  );
}
